const ENCRYPTED_VIDEO_URL_PREFIX = '__CREATOR_DOST_VIDEO_V1__:';
const VIDEO_KEY_INFO = new TextEncoder().encode('creator-dost-video-url-v1');

function toBase64(bytes) {
  return btoa(String.fromCharCode(...new Uint8Array(bytes)));
}

function fromBase64(value) {
  const standardBase64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const paddedBase64 = standardBase64.padEnd(
    standardBase64.length + ((4 - (standardBase64.length % 4)) % 4),
    '=',
  );
  return Uint8Array.from(atob(paddedBase64), (character) => character.charCodeAt(0));
}

export async function createVideoUrlCryptoContext() {
  if (!window.crypto?.subtle) {
    throw new Error('Secure video links require a secure browser connection.');
  }

  const keyPair = await window.crypto.subtle.generateKey(
    { name: 'ECDH', namedCurve: 'P-256' },
    false,
    ['deriveBits'],
  );
  const publicKey = await window.crypto.subtle.exportKey('raw', keyPair.publicKey);

  return {
    clientPrivateKey: keyPair.privateKey,
    headers: {
      'X-Video-Client-Key': toBase64(publicKey),
    },
  };
}

async function decryptVideoUrl(value, clientPrivateKey) {
  const encodedPacket = value.slice(ENCRYPTED_VIDEO_URL_PREFIX.length);
  const packet = JSON.parse(new TextDecoder().decode(fromBase64(encodedPacket)));
  if (!packet?.serverPublicKey || !packet.salt || !packet.iv || !packet.ciphertext) {
    throw new Error('The video API returned an invalid encrypted link.');
  }

  const serverPublicKey = await window.crypto.subtle.importKey(
    'raw',
    fromBase64(packet.serverPublicKey),
    { name: 'ECDH', namedCurve: 'P-256' },
    false,
    [],
  );
  const sharedSecret = await window.crypto.subtle.deriveBits(
    { name: 'ECDH', public: serverPublicKey },
    clientPrivateKey,
    256,
  );
  const hkdfKey = await window.crypto.subtle.importKey(
    'raw',
    sharedSecret,
    'HKDF',
    false,
    ['deriveKey'],
  );
  const encryptionKey = await window.crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: fromBase64(packet.salt),
      info: VIDEO_KEY_INFO,
    },
    hkdfKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['decrypt'],
  );
  const decrypted = await window.crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: fromBase64(packet.iv),
      additionalData: VIDEO_KEY_INFO,
      tagLength: 128,
    },
    encryptionKey,
    fromBase64(packet.ciphertext),
  );

  const videoUrl = new TextDecoder().decode(decrypted);
  const videoId = extractYouTubeVideoId(videoUrl);
  if (!videoId) {
    return videoUrl;
  }

  const embedUrl = new URL(`https://www.youtube-nocookie.com/embed/${videoId}`);
  try {
    const sourceUrl = new URL(videoUrl);
    ['autoplay', 'controls', 'enablejsapi', 'mute', 'playsinline', 'rel', 'modestbranding'].forEach(
      (parameter) => {
        const value = sourceUrl.searchParams.get(parameter);
        if (value !== null) {
          embedUrl.searchParams.set(parameter, value);
        }
      },
    );
  } catch {
    // Bare YouTube video IDs need no query parameters.
  }

  return embedUrl.toString();
}

export async function decryptVideoUrlsInResponse(value, clientPrivateKey) {
  // alert(value+"");
  if (typeof value === 'string') {
    return value.startsWith(ENCRYPTED_VIDEO_URL_PREFIX)
      ? decryptVideoUrl(value, clientPrivateKey)
      : value;
  }

  if (Array.isArray(value)) {
    return Promise.all(
      value.map((item) => decryptVideoUrlsInResponse(item, clientPrivateKey)),
    );
  }

  if (value && typeof value === 'object') {
    const entries = await Promise.all(
      Object.entries(value).map(async ([key, item]) => [
        key,
        await decryptVideoUrlsInResponse(item, clientPrivateKey),
      ]),
    );
    return Object.fromEntries(entries);
  }
  //  alert(value+"");
  return value;
}

export function extractYouTubeVideoId(source) {
  const value = String(source || '').trim();
  if (/^[A-Za-z0-9_-]{11}$/.test(value)) {
    return value;
  }

  let url;
  try {
    url = new URL(value);
  } catch {
    return null;
  }

  if (url.protocol !== 'https:') {
    return null;
  }

  const hostname = url.hostname.toLowerCase();
  let videoId = '';
  if (hostname === 'youtu.be') {
    videoId = url.pathname.split('/').filter(Boolean)[0] || '';
  } else if (
    ['youtube.com', 'www.youtube.com', 'm.youtube.com', 'youtube-nocookie.com', 'www.youtube-nocookie.com'].includes(hostname)
  ) {
    if (url.pathname === '/watch') {
      videoId = url.searchParams.get('v') || '';
    } else {
      const pathParts = url.pathname.split('/').filter(Boolean);
      if (['embed', 'shorts', 'live'].includes(pathParts[0])) {
        videoId = pathParts[1] || '';
      }
    }
  }

  return /^[A-Za-z0-9_-]{11}$/.test(videoId) ? videoId : null;
}
